/* eslint-disable no-unused-vars */
import { useEffect, useCallback, useMemo } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  faEdit,
  faTrash,
  faEye,
  faUndo,
} from '@fortawesome/free-solid-svg-icons'
import { maskCpfCnpj } from '../../../utils/maskUtils'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useBaseService from '../../../hooks/services/useBaseService'
import ListHeader from '../../../components/ListHeader'
import PageHeader from '../../../components/PageHeader'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import AutoCompleteFilter from '../../../components/AutoCompleteFilter'
import baseService from '../../../services/baseService'
import { entities } from '../../../constants/entities'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useAction from '../../../hooks/useAction' // Importar useAction
import useFilterHandlers from '../../../hooks/useFilterHandlers'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import FilterCollapse from '../../../components/FilterCollapse'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { useOrgan } from '../../../hooks/useOrgan'

const CustomerPage = () => {
  const { canAccess } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()
  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()
  const { get: baseServiceGet, del: baseServiceDel } = useBaseService(navigate)
  const queryClient = useQueryClient()

  const { selectedOrgan } = useOrgan()

  // Gerenciamento de filtros de URL
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      alias: [],
      name: [],
      cpf_cnpj: [],
      deleted_at: false,
    },
    'customers'
  )

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters)

  // Gerenciamento de filtros locais (para o FilterForm)
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        alias: [],
        name: [],
        cpf_cnpj: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  const {
    data: customerData,
    isLoading,
    isFetching,
  } = useQueryWithLoader({
    queryKey: ['customers', filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl({
        ...filters,
        alias: { $nin: ['ALUCOM', 'MOREIA', 'IP', 'AS SISTEMAS'] },
      })
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
        sort: { column: 'updated_at', order: 'desc' },
      }
      const response = await baseServiceGet(entities.customers.get, params)
      return response.result
    },
    placeholderData: (previousData) => previousData,
    staleTime: 60 * 1000,
    keepPreviousData: true,
    select: (data) => ({
      customers: data.data.map((customer) => {
        const numericValue = customer.cpf_cnpj
          ? customer.cpf_cnpj.replace(/\D/g, '')
          : ''
        return {
          id: customer.id,
          alias: customer.alias,
          name: customer.name,
          cpf_cnpj: customer.cpf_cnpj ? maskCpfCnpj(customer.cpf_cnpj) : '',
          status: customer.deleted_at ? 'Inativo' : 'Ativo',
          has_contracts_in_progress_or_closed:
            !!customer.has_contracts_in_progress_or_closed,
          has_equipments_in_use: !!customer.has_equipments_in_use,
          isDeleted: !!customer.deleted_at,
        }
      }),
      totalPages: data.last_page,
    }),
  })

  const { customers = [], totalPages = 1 } = customerData || {}

  const columns = [
    { key: 'id', label: 'Número', width: '5%' },
    { key: 'alias', label: 'Nome Fantasia', width: '50%' },
    { key: 'cpf_cnpj', label: 'CPF/CNPJ' }, //Coluna não é mais numerica com a alteração para alfa-numerico
  ]

  // --- Função para Forçar o Refetch da Lista (invalida o cache) ---
  const refreshList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['customers'] })
  }, [queryClient])

  // --- useAction para gerenciamento de modal de confirmação e ações gerais ---
  const {
    openModalConfirmation,
    action,
    handleDelete,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate)

  const inactiveMutation = useMutation({
    mutationFn: async (customerId) => {
      showLoader()
      const response = await baseServiceDel(
        entities.customers.delete(customerId)
      )
      return response
    },
    onSuccess: (data) => {
      if (data?.status === 200) {
        hideLoader()
        refreshList() // Refresca a lista após sucesso
      }
      hideLoader()
    },
    onError: (err) => {
      console.error('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader() // Esconde loader no final
      handleCancelConfirmation() // Fecha o modal de confirmação
    },
  })

  const activateMutation = inactiveMutation

  // Efeito para exibir notificações da rota (se houver)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const actions = useMemo(
    () => [
      {
        id: 'details',
        icon: faEye,
        title: 'Ver Detalhes',
        buttonClass: 'btn-info',
        permission: 'Ver clientes',
        onClick: (customer) =>
          navigate(`/aluguel/clientes/detalhes/${customer.id}`),
        show: (customer) => !customer.isDeleted && canAccess('Ver clientes'),
      },
      {
        id: 'edit',
        icon: faEdit,
        title: 'Editar',
        buttonClass: 'btn-primary',
        permission: 'Atualizar clientes',
        onClick: (customer) =>
          navigate(`/aluguel/clientes/editar/${customer.id}`),
        show: (customer) =>
          !customer.isDeleted && canAccess('Atualizar clientes'),
      },
      {
        id: 'delete',
        icon: faTrash,
        title: 'Excluir',
        buttonClass: 'btn-danger',
        permission: 'Excluir clientes',
        onClick: (customer) =>
          showConfirmation(
            customer,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactiveMutation.mutateAsync(customer.id)
              } catch (error) {
                console.error('Erro ao excluir cliente:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (customer) =>
          !customer.isDeleted &&
          (!customer.has_contracts_in_progress_or_closed ||
            !customer.has_equipments_in_use) &&
          canAccess('Excluir clientes'),
      },
      {
        id: 'activate',
        icon: faUndo,
        title: 'Restaurar',
        buttonClass: 'btn-info',
        permission: 'Atualizar clientes',
        onClick: (customer) =>
          showConfirmation(
            customer,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateMutation.mutateAsync(customer.id)
              } catch (error) {
                console.error('Erro ao restaurar cliente:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (customer) =>
          customer.isDeleted && canAccess('Atualizar clientes'),
      },
    ],
    [navigate, canAccess, showConfirmation, inactiveMutation, activateMutation] // Adicionar refreshList como dependência
  )

  const inputsfilters = useMemo(
    () => [
      {
        label: 'Cliente',
        gridSize: '12',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase={['id', 'alias', 'cpf_cnpj']}
            model="customer"
            value={localFilterValues.name}
            onChange={(selected) => {
              const val = selected?.value
              handleFilterChange('id', val ? { $eq: val } : null)
            }}
            placeholder="Filtre os clientes pelo nome ou CPF/CNPJ"
          />
        ),
      },
    ],
    [localFilterValues, handleFilterChange]
  )

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Clientes" showBackButton={true} backUrl="/dashboard" />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />
        <ListHeader
          title="Lista de Clientes"
          buttonText="Novo Cliente"
          buttonLink="/aluguel/clientes/criar"
          canAccess={canAccess}
          permission="Criar clientes"
        />

        <TableWithInactiveFilter
          data={customers}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoading}
          isFetching={isFetching}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ''}
          text={action.text}
        />
      </div>
    </MainLayout>
  )
}

export default CustomerPage
